import React from 'react';
import DoctorProfileCard from './profiles/DoctorProfileCard';
import HealthcareProviderCard from './profiles/HealthcareProviderCard';
import LoadingSkeleton from './LoadingSkeleton';

const SearchResults = ({ results, searchType, searchQuery, isLoading }) => {
  
  // Loading state
  if (isLoading) {
    return <LoadingSkeleton searchType={searchType} />;
  }

  // No results state
  if (!results.total && searchQuery) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
        <p className="text-gray-500 mb-4">
          We couldn't find any results for "{searchQuery}". Try:
        </p>
        <ul className="text-sm text-gray-600 space-y-1 max-w-md mx-auto">
          <li>• Checking spelling or using different keywords</li>
          <li>• Searching for broader terms (e.g., "doctor" instead of specific name)</li>
          <li>• Removing location filters to see more results</li>
          <li>• Using medical specialties like "cardiology" or "pediatrics"</li>
        </ul>
      </div>
    );
  }

  // Welcome/initial state
  if (!results.total && !searchQuery) {
    return (
      <div className="text-center py-12">
        <div className="mb-6">
          <div className="text-4xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Healthcare Search</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Start typing to find doctors, specialists, or healthcare providers. 
            Our intelligent search will suggest relevant matches as you type.
          </p>
        </div>
        
        {/* Popular searches */}
        <div className="max-w-2xl mx-auto">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Popular Searches:</h4>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              'Cardiologist', 'Family Doctor', 'Urgent Care', 'Dermatologist',
              'Pediatrician', 'Emergency Room', 'Physical Therapy', 'Dentist'
            ].map(term => (
              <button
                key={term}
                className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                onClick={() => window.dispatchEvent(new CustomEvent('popularSearchClick', { detail: term }))}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Results Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {searchQuery ? `Results for "${searchQuery}"` : 'Healthcare Providers'}
            </h2>
            <p className="text-gray-600 mt-1">
              Found {results.total} result{results.total !== 1 ? 's' : ''}
              {searchType !== 'all' && ` in ${searchType.replace('-', ' ')}`}
            </p>
          </div>
          
          {/* Sort Options */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Sort by:</label>
            <select className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="relevance">Relevance</option>
              <option value="rating">Highest Rated</option>
              <option value="distance">Distance</option>
              <option value="availability">Availability</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Grid */}
      <div className="space-y-4">
        {/* Doctor Results */}
        {(searchType === 'all' || searchType === 'doctors') && results.doctors.length > 0 && (
          <div>
            {searchType === 'all' && (
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="mr-2">👨‍⚕️</span>
                  Doctors ({results.doctors.length})
                </h3>
                <div className="h-px bg-gray-200 mt-2"></div>
              </div>
            )}
            
            <div className="grid gap-4">
              {results.doctors.map(doctor => (
                <DoctorProfileCard
                  key={doctor.id}
                  doctor={doctor}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </div>
        )}

        {/* Healthcare Provider Results */}
        {(searchType === 'all' || searchType === 'healthcare-providers') && results.healthcareProviders.length > 0 && (
          <div className={searchType === 'all' && results.doctors.length > 0 ? 'mt-8' : ''}>
            {searchType === 'all' && (
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="mr-2">🏥</span>
                  Healthcare Providers ({results.healthcareProviders.length})
                </h3>
                <div className="h-px bg-gray-200 mt-2"></div>
              </div>
            )}
            
            <div className="grid gap-4">
              {results.healthcareProviders.map(provider => (
                <HealthcareProviderCard
                  key={provider.id}
                  provider={provider}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Load More Button */}
      {results.total > 10 && (
        <div className="text-center mt-8">
          <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Load More Results
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;